import {
  Modal,
  Box,
  Grid,
  Divider,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAlert from 'components/MDAlert';
import axios from 'axios';
import { useUser } from 'context/user';
import { FIREBASE_API_URL, PRICE_PER_TOKEN } from 'config';

interface SubscriptionOption {
  id: string;
  label: string;
  credits: number;
  months: number;
}

interface SubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  isActive: boolean;
  onSuccess: () => void;
}

const SubscriptionModal = ({
  open,
  onClose,
  isActive,
  onSuccess,
}: SubscriptionModalProps) => {
  const { user, accessToken } = useUser();
  const [selectedOptionId, setSelectedOptionId] = useState<string>('1');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const subscriptionOptions: SubscriptionOption[] = [
    {
      id: '1',
      label: '1 Month',
      credits: 1 * PRICE_PER_TOKEN * user.priceModifiers[1],
      months: 1,
    },
    {
      id: '3',
      label: '3 Months',
      credits: 3 * PRICE_PER_TOKEN * user.priceModifiers[3],
      months: 3,
    },
    {
      id: '12',
      label: '1 Year',
      credits: 12 * PRICE_PER_TOKEN * user.priceModifiers[12],
      months: 12,
    },
  ];

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedOptionId('1');
      setError(null);
      setSuccess(null);
      setLoading(false);
    }
  }, [open]);

  const handleOptionChange = (event: SelectChangeEvent) => {
    setSelectedOptionId(event.target.value);
  };

  const selectedOption = subscriptionOptions.find(
    (option) => option.id === selectedOptionId
  );

  const hasEnoughCredits =
    selectedOption && user?.credits >= selectedOption.credits;

  const handleUpdateSubscription = async () => {
    if (!selectedOption || !hasEnoughCredits) {
      setError('Insufficient credits for this subscription option');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${FIREBASE_API_URL}/api/subscription/update`,
        {
          accessToken,
          durationMonths: selectedOption.months,
        }
      );

      if (response.data.success) {
        setSuccess('Subscription updated successfully');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to update subscription');
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      setError('An error occurred while updating your subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${FIREBASE_API_URL}/api/subscription/cancel`,
        {
          accessToken,
        }
      );

      if (response.data.success) {
        setSuccess('Subscription cancelled successfully');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('An error occurred while cancelling your subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="subscription-modal"
      aria-describedby="modal-for-managing-subscription"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
          maxWidth: '600px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <MDTypography variant="h5" fontWeight="medium">
            {isActive ? 'Update Subscription' : 'New Subscription'}
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <MDAlert color="error" mb={2}>
            {error}
          </MDAlert>
        )}

        {success && (
          <MDAlert color="success" mb={2}>
            {success}
          </MDAlert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="subscription-option-label">
                Subscription Duration
              </InputLabel>
              <Select
                labelId="subscription-option-label"
                id="subscription-option"
                value={selectedOptionId}
                label="Subscription Duration"
                onChange={handleOptionChange}
                disabled={loading}
              >
                {subscriptionOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <MDBox
              p={2}
              bgcolor="background.neutral"
              borderRadius="1"
              sx={{ backgroundColor: '#f8f9fa' }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant="body2" fontWeight="bold">
                    Duration:
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="body2">
                    {selectedOption?.label}
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="body2" fontWeight="bold">
                    Cost:
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="body2">
                    {selectedOption.credits.toFixed(2)} credits
                  </MDTypography>
                </Grid>
                {user.priceModifiers[
                  selectedOptionId as keyof typeof user.priceModifiers
                ] &&
                  user.priceModifiers[
                    selectedOptionId as keyof typeof user.priceModifiers
                  ] !== 1 && (
                    <Grid item xs={6}>
                      <MDTypography variant="body2" fontWeight="bold">
                        Save:
                      </MDTypography>
                    </Grid>
                  )}
                {user.priceModifiers[
                  selectedOptionId as keyof typeof user.priceModifiers
                ] &&
                  user.priceModifiers[
                    selectedOptionId as keyof typeof user.priceModifiers
                  ] !== 1 && (
                    <Grid item xs={6}>
                      <MDTypography variant="body2">
                        {(
                          PRICE_PER_TOKEN * selectedOption.months -
                          PRICE_PER_TOKEN *
                            selectedOption.months *
                            user.priceModifiers[
                              selectedOptionId as keyof typeof user.priceModifiers
                            ]
                        ).toFixed(2)}{' '}
                        credits
                      </MDTypography>
                    </Grid>
                  )}
                <Grid item xs={6}>
                  <MDTypography variant="body2" fontWeight="bold">
                    Your Balance:
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography
                    variant="body2"
                    color={hasEnoughCredits ? 'success' : 'error'}
                  >
                    {user?.credits?.toFixed(2) || 0} credits
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={4} display="flex" justifyContent="space-between">
          {isActive && (
            <MDButton
              variant="outlined"
              color="error"
              onClick={handleCancelSubscription}
              disabled={loading}
            >
              Cancel Subscription
            </MDButton>
          )}
          <MDBox ml="auto" display="flex" gap={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Close
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleUpdateSubscription}
              disabled={loading || !hasEnoughCredits}
            >
              {isActive ? 'Update Subscription' : 'Subscribe'}
            </MDButton>
          </MDBox>
        </MDBox>
      </Box>
    </Modal>
  );
};

export default SubscriptionModal;
